import { ToastType } from '@components/toast';
import { DocumentStatus } from '@models/document';
import { useCreateDocumentGeneration } from '@services/document/useCreateDocumentGeneration';
import { useSearchDocuments } from '@services/document/useSearchDocuments';

import { isBefore } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

export const useDownloadQuoteDocument = ({
  quoteId,
  createdOnOrAfterDate,
  documentTypeId,
  packageTypeId,
  downloadType,
  disabled,
}: {
  quoteId: string;
  createdOnOrAfterDate?: Date;
  documentTypeId: string;
  packageTypeId: string;
  /** If auto will auto attempt to download the document on completion */
  downloadType: 'manual' | 'auto';
  disabled?: boolean;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { createDocumentGenerationAsync, isError: isErrorCreating } = useCreateDocumentGeneration();

  const [documentGenerationId, setDocumentGenerationId] = useState<string | undefined>();
  const [triggerDownloadDate, setTriggerDownloadDate] = useState<Date | undefined>();

  const {
    documents,
    isError: isErrorSearching,
    isLoading: isLoadingSearch,
  } = useSearchDocuments({
    quoteId,
    createdOnOrAfterDate: documentGenerationId ? undefined : (triggerDownloadDate ?? createdOnOrAfterDate),
    documentTypeId: [documentTypeId],
    documentGenerationId,
  });

  const lastDocumentCreated = documents?.items.length ? documents.items[0] : undefined;
  const downloadUrl = lastDocumentCreated?.file?.downloadUrl;

  const isPendingOrInProgress =
    !lastDocumentCreated ||
    lastDocumentCreated.documentGeneration?.status === DocumentStatus.InProgress ||
    lastDocumentCreated.documentGeneration?.status === DocumentStatus.Pending;

  const handleDownloadQuoteDocument = async () => {
    if (disabled) {
      return;
    }

    const downloadTime = new Date();
    setTriggerDownloadDate(downloadTime);

    if (!downloadUrl) {
      const documentGenerationResponse = await createDocumentGenerationAsync({
        packageTypeId,
        quoteId,
      });

      if (!documentGenerationResponse.id) {
        return;
      }

      setDocumentGenerationId(documentGenerationResponse.id);
    }
  };

  useEffect(() => {
    if (isPendingOrInProgress) {
      return;
    }

    if (triggerDownloadDate) {
      if (lastDocumentCreated?.documentGeneration?.status === DocumentStatus.Failed) {
        const documentFailedAfterCreateTrigger = isBefore(triggerDownloadDate, new Date(lastDocumentCreated.createdAt));

        if (documentFailedAfterCreateTrigger) {
          enqueueSnackbar('No document was generated', {
            variant: ToastType.Error,
          });

          setTriggerDownloadDate(undefined);
          setDocumentGenerationId(undefined);
        }
      } else if (lastDocumentCreated?.documentGeneration?.status === DocumentStatus.Completed && downloadUrl) {
        if (downloadType === 'manual') {
          setTriggerDownloadDate(undefined);
          setDocumentGenerationId(undefined);
          return;
        }
        const link = document.createElement('a');
        link.target = '_blank';
        link.href = downloadUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTriggerDownloadDate(undefined);
        setDocumentGenerationId(undefined);
      }
    }
  }, [downloadType, triggerDownloadDate, downloadUrl, lastDocumentCreated, isPendingOrInProgress, enqueueSnackbar]);

  const isError = isErrorCreating || isErrorSearching;
  const isLoading = !isError && isPendingOrInProgress && !!triggerDownloadDate;

  const documentFormSetId = lastDocumentCreated?.documentFormSetId;

  return { isLoading, isDisabled: isLoadingSearch, handleDownloadQuoteDocument, downloadUrl, documentFormSetId };
};
